import { Capacitor } from '@capacitor/core';

/**
 * Géolocalisation unifiée web / natif.
 *
 *  - Sur Android/iOS (Capacitor) : utilise @capacitor/geolocation, qui demande
 *    la permission système native puis lit le GPS. Indispensable car, dans une
 *    WebView Capacitor, `navigator.geolocation` n'est pas relié au système de
 *    permissions natif et échoue silencieusement (« géolocalisation impossible »).
 *  - Sur le web : retombe sur `navigator.geolocation`.
 *
 * Résout toujours `{ lat, lon }` ou rejette avec une Error explicite
 * (`permission_denied`, `unsupported`, ou l'erreur d'origine).
 */

const isNative = Capacitor.isNativePlatform();

export const getPosition = async ({ timeout = 8000, highAccuracy = false } = {}) => {
  if (isNative) {
    const { Geolocation } = await import('@capacitor/geolocation');

    // Demande la permission si elle n'est pas déjà accordée.
    let perm = await Geolocation.checkPermissions();
    const granted = (p) => p?.location === 'granted' || p?.coarseLocation === 'granted';
    if (!granted(perm)) {
      perm = await Geolocation.requestPermissions({ permissions: ['location', 'coarseLocation'] });
    }
    if (!granted(perm)) {
      throw new Error('permission_denied');
    }

    const pos = await Geolocation.getCurrentPosition({
      enableHighAccuracy: highAccuracy,
      timeout,
      maximumAge: 3600000,
    });
    return { lat: pos.coords.latitude, lon: pos.coords.longitude };
  }

  // --- Web ---
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('unsupported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      (err) => reject(err),
      { timeout, enableHighAccuracy: highAccuracy, maximumAge: 3600000 }
    );
  });
};
